import apiClient from 'apiClient/index';
import { PageOfferVars } from 'constants/types/pageOffer';
import {
  action,
  configure, makeAutoObservable, observable,
  runInAction,
} from 'mobx';
import { TSupportedCountryCodes, TSupportedLocales } from 'petdna-lib-uicomponents/constants';
import {
  EUnionPageName,
  ForProject,
  IKitPrices,
  IShoppingCart,
  ISiteFooterVariables,
  ISiteHeader,
  ISiteHeaderVariables,
  IUnionPage,
} from 'petdna-lib-uicomponents/hygraph';
import { ITopPagePromoBanner } from 'petdna-lib-uicomponents/types';
import {
  ICookieDisclosure,
  IPageHome,
  IPageLoggedInDashboard,
  IPageOffer,
  IPagePartnerships,
  TNavbar,
  TPageCoBrandedSections,
  TPageConsent,
  TPolicyPage,
} from 'stores/types/Pages';
import {
  ISectionDashboardCarousel,
  ISectionDashboardPartnership,
} from 'stores/types/Sections';

import { ISurveys } from './types/Survey';

type IUnionPageStore<T> = {
  [key: string]: T;
};

const generateUnionPageStore = <T>(initialValue: T): IUnionPageStore<T> => {
  const store: IUnionPageStore<T> = {} as IUnionPageStore<T>;
  return Object.keys(EUnionPageName).reduce((o, k) => ({ ...o, [k]: initialValue }), store);
};

configure({
  enforceActions: 'observed',
  isolateGlobalState: true,
  reactionRequiresObservable: true,
});

class PagesStore {
  pageConsent: TPageConsent | null = null;
  pageConsentLoaded = false;
  pageMarketingConsent: TPageConsent | null = null;
  pageMarketingConsentLoaded = false;
  pageHome: IPageHome | null = null;
  pageHomeLoaded = false;
  pageLoggedInDashboard: IPageLoggedInDashboard | null = null;
  pageLoggedInDashboardLoaded: boolean = false;
  pagePartnerships: IPagePartnerships | null = null;
  pagePartnershipsLoaded: boolean = false;
  navbarData: TNavbar | null = null;
  isNavbarLoaded: boolean = false;
  siteHeaders: Map<string, ISiteHeader> = new Map();
  siteHeaderData: ISiteHeader | undefined = undefined;
  isSiteHeaderLoaded: boolean = false;
  siteFooters: Map<string, ISiteHeader> = new Map();
  footerData: any = null;
  kitPrices: IKitPrices[] = [];
  unionPages: IUnionPageStore<IUnionPage | null> = generateUnionPageStore<IUnionPage | null>(null);
  areKitPricesLoaded: boolean = false;
  policyData: TPolicyPage | null = null;
  cookiesMessage: ICookieDisclosure | null = null;
  pageOffer: IPageOffer | null = null;
  pageOfferLoaded = false;
  pageCoBranded: TPageCoBrandedSections | [] = [];
  pageCoBrandedLoaded: boolean = false;
  promoBanners: ITopPagePromoBanner[] | null = null;
  promoBannersLoaded: boolean = false;
  unionPagesLoaded: IUnionPageStore<boolean> = generateUnionPageStore<boolean>(false);
  sectionDashboardPartnership: ISectionDashboardPartnership | null = null;
  sectionDashboardCarousel: ISectionDashboardCarousel | null = null;
  isSurveyListLoading: boolean = false;
  surveyList: ISurveys | null = null;
  shoppingCart: IShoppingCart | undefined = undefined;
  isShoppingCartLoaded = false;

  constructor() {
    makeAutoObservable(this, {
      pageHome: observable,
      pageHomeLoaded: observable,
      pageLoggedInDashboard: observable,
      pageLoggedInDashboardLoaded: observable,
      navbarData: observable,
      isNavbarLoaded: observable,
      footerData: observable,
      kitPrices: observable,
      areKitPricesLoaded: observable,
      policyData: observable,
      sectionDashboardPartnership: observable,
      getKitPrices: action,
      cookiesMessage: observable,
      pageOfferLoaded: observable,
      pageOffer: observable,
      getPageOffer: action,
      getSectionDashboardPartnership: action,
      surveyList: observable,
      getSurveyList: action,
      getSiteFooter: action,
      siteFooters: observable,
    });
  }

  getPageConsent = async () => {
    if (!this.pageConsentLoaded) {
      const response = await apiClient.page.getRegistrationConsent();
      runInAction(() => {
        if (response?.data?.data?.pageRegistrationConsent) {
          this.pageConsent = response.data.data.pageRegistrationConsent;
        }
        this.pageConsentLoaded = true;
      });
    }
  };

  getMarketingConsents = async () => {
    if (!this.pageMarketingConsentLoaded) {
      const response = await apiClient.page.getRegistrationMarketingConsent();
      runInAction(() => {
        if (response?.data?.data?.pageRegistrationConsent) {
          this.pageMarketingConsent = response.data.data.pageRegistrationConsent;
        }
        this.pageMarketingConsentLoaded = true;
      });
    }

    return this.pageMarketingConsent;
  };

  getPageHome = async (locale: TSupportedLocales[]) => {
    if (!this.pageHomeLoaded) {
      const response = await apiClient.page.getPageHome(locale);
      if (response?.data?.data?.pageLiMarketing) {
        this.pageHome = response.data.data.pageLiMarketing;
      }
      this.pageHomeLoaded = true;
    }
  };

  getPageOffer = async (
    variables: PageOfferVars,
    locale: TSupportedLocales[],
  ) => {
    if (!this.pageOfferLoaded) {
      const response = await apiClient.page.getPageOffer(variables, locale);
      if (response?.data?.data?.pageLiOffer) {
        this.pageOffer = response.data.data.pageLiOffer;
      }
      this.pageOfferLoaded = true;
    }
  };

  getPageLoggedInDashboard = async () => {
    if (!this.pageLoggedInDashboard) {
      const response = await apiClient.page.getPageLoggedInDashboard();
      runInAction(() => {
        if (response?.data?.data?.pageLiDashboard) {
          this.pageLoggedInDashboard = response?.data?.data.pageLiDashboard;
        }
        this.pageLoggedInDashboardLoaded = true;
      });
    }
  };

  getPartnerships = async (locale: TSupportedLocales[]) => {
    if (!this.pagePartnershipsLoaded) {
      const response = await apiClient.page.getPagePartnerships(locale);
      runInAction(() => {
        if (response.data.data.pagePartnership) {
          this.pagePartnerships = response?.data?.data.pagePartnership;
        }
        this.pagePartnershipsLoaded = true;
      });
    }
  };

  getSurveyList = async () => {
    if (this.isSurveyListLoading) return;
    this.isSurveyListLoading = true;
    const response = await apiClient.page.getSurveyList();
    runInAction(() => {
      this.isSurveyListLoading = false;
      if (response.data.data) {
        this.surveyList = response.data.data;
      }
    });
  };

  getSiteFooter = async (countryCode: TSupportedCountryCodes, forProject: ForProject) => {
    const key = `${forProject}:${countryCode}`;
    if (this.siteHeaders.has(key)) {
      this.siteHeaderData = this.siteHeaders.get(key);
      return;
    }

    const variables: ISiteFooterVariables = {
      countryCode: [countryCode],
      forProject,
    };
    const response = await apiClient.page.getSiteFooter(variables);

    runInAction(() => {
      if (response?.data?.data?.siteFooters) {
        const siteFooter = response?.data?.data?.siteFooters
          .find((item) => item.countryCode?.includes(countryCode))
          || response?.data?.data?.siteFooters[0];

        this.siteFooters.set(key, siteFooter);
        this.footerData = siteFooter;
      }
    });
  };

  getSiteHeader = async (countryCode: TSupportedCountryCodes, forProject: ForProject) => {
    const key = `${forProject}:${countryCode}`;
    if (this.siteHeaders.has(key)) {
      this.siteHeaderData = this.siteHeaders.get(key);
      return;
    }

    const variables: ISiteHeaderVariables = {
      countryCode: [countryCode],
      forProject,
      kitsSaleCountry: countryCode,
    };
    const response = await apiClient.page.getSiteHeader(variables);

    runInAction(() => {
      if (response?.data?.data?.siteHeaders) {
        const siteHeader = response?.data?.data?.siteHeaders
          .find((item) => item.countryCode?.includes(countryCode))
          || response?.data?.data?.siteHeaders[0];

        this.siteHeaders.set(key, siteHeader);
        this.siteHeaderData = siteHeader;
      }
    });
  };

  getShoppingCart = async (id: string, countryCode: TSupportedCountryCodes) => {
    if (this.isShoppingCartLoaded) return;

    const response = await apiClient.page.getShoppingCart(id, countryCode);
    runInAction(() => {
      this.isShoppingCartLoaded = true;
      if (response?.data?.data?.shoppingCart) {
        this.shoppingCart = response.data.data.shoppingCart;
      }
    });
  };

  getKitPrices = async () => {
    if (!this.areKitPricesLoaded) {
      this.areKitPricesLoaded = true;
      const response = await apiClient.page.getKitPrices();
      if (response?.data?.data?.kitPrices) {
        this.kitPrices = response.data.data.kitPrices;
      }
    }
  };

  getPolicyData = async (policyId: string) => {
    const policyData = await apiClient.page.getPolicy(policyId);
    if (policyData?.data) {
      this.policyData = policyData?.data?.data?.policy;
    }
  };

  getCookiesDisclosureMessage = async () => {
    const cookiesMessage = await apiClient.page.getCookiesDisclosure();
    if (cookiesMessage?.data) {
      this.cookiesMessage = cookiesMessage?.data?.data?.pageAccount;
    }
  };

  getCoBranded = async (name: string | null, locale: TSupportedLocales) => {
    if (this.pageCoBrandedLoaded) return;

    if (!name) {
      this.pageCoBrandedLoaded = true;
      return;
    }

    const response = await apiClient.page.getPageCoBranded(name, locale);
    runInAction(() => {
      if (response?.data?.data.pageCoBranded) {
        this.pageCoBranded = response?.data?.data.pageCoBranded.sections;
      }
      this.pageCoBrandedLoaded = true;
    });
  };

  getPromoBanners = async (
    enabled: boolean,
    locale: TSupportedLocales[],
  ) => {
    if (this.promoBannersLoaded) {
      return;
    }

    const response = await apiClient.page.getPromoBanners(enabled, locale, 'LIHP');
    runInAction(() => {
      this.promoBanners = response?.data?.data.promoBanners ?? [];
      this.promoBannersLoaded = true;
    });
  };

  getSectionDashboardPartnership = async () : Promise<ISectionDashboardPartnership | null> => {
    if (this.sectionDashboardPartnership) {
      return this.sectionDashboardPartnership;
    }

    const response = await apiClient.page.getSectionDashboardPartnership();
    return runInAction(() => {
      if (response?.data?.data.sectionDashboardPartnerships) {
        this.sectionDashboardPartnership = response?.data?.data.sectionDashboardPartnerships[0]
          ?? null;
      }

      return this.sectionDashboardPartnership;
    });
  };

  getSectionDashboardCarousel = async (
    locales: TSupportedLocales[],
  ): Promise<ISectionDashboardCarousel | null> => {
    if (this.sectionDashboardCarousel) {
      return this.sectionDashboardCarousel;
    }

    const response = await apiClient.page.getSectionDashboardCarousel(locales);
    return runInAction(() => {
      if (response?.data?.data.sectionDashboardCarousels) {
        this.sectionDashboardCarousel = response.data.data.sectionDashboardCarousels[0] ?? null;
      }

      return this.sectionDashboardCarousel;
    });
  };

  getUnionPage = async (
    page: EUnionPageName | string,
    countryCode: TSupportedCountryCodes,
    omitLocale = false,
    pageSectionQueries?: string[],
  ) => {
    if (!this.unionPagesLoaded[page]) {
      const response = await apiClient.page.getUnionPage({
        page,
        omitLocale,
        pageSectionQueries,
        countryCode,
      });
      this.unionPages[page] = response.data.data.unionPages.find(
        (u) => u.countryCode?.includes(countryCode),
      ) ?? response.data.data.unionPages[0];
      this.unionPagesLoaded[page] = true;
    }
  };
}

export default PagesStore;
